import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material'
import { CloseRounded, ZoomInRounded } from '@mui/icons-material'
import { format as formatDate } from 'date-fns'
import { deleteEvidenceImage, getEvidenceImage, getEvidenceViews } from './visionApi'
import { whenText } from './visionIncidentFormat'

const BOX_COLOUR = '#FF8F00'
// Design Registro v4 (C17b): red only on the controls that delete.
const DELETE_COLOUR = '#F68D76'
const DAY_MS = 24 * 60 * 60 * 1000
const NO_VIEWS = { items: [], total: 0 }

// RN161: why a phone image was not saved.
const FAILURE_TEXT = {
  NO_FRAME: 'No frame was available',
  BLUR_FAILED: 'Faces could not be blurred',
  SAVE_ERROR: 'The image could not be saved'
}

// S394, S414: what Sun answered when the image was asked for, or deleted, and it did not (E18, E19, E20); MIC's own notices
// for 403 and 500.
const REFUSAL_TEXT = { 404: 'Phone image not available', 403: 'Permission denied', 400: 'Invalid image selection' }
const FAILED_IMAGE_TEXT = 'Something went wrong'
export const refusalText = (status, t_) => t_(REFUSAL_TEXT[status] || FAILED_IMAGE_TEXT)

// The line that replaces the data when there is no image to see (S392).
const lineWithoutImage = (evidence, dateFormat, t_) => {
  if (!evidence) {
    return t_('Phone image not available')
  }
  // C17b (S412, DI-10): deleted by hand says who and when, unlike an expired one.
  if (evidence.status === 'DELETED_BY_ADMIN') {
    return t_('Deleted by {{name}} on {{date}}', { name: evidence.deletedBy || t_('Unknown'), date: formatDate(new Date(evidence.deletedAt), dateFormat) })
  }
  if (evidence.status === 'EXPIRED') {
    const days = Math.round((Date.parse(evidence.expiresAt) - Date.parse(evidence.capturedAt)) / DAY_MS)
    return t_('Image deleted after {{days}} days', { days })
  }
  if (evidence.status === 'FAILED') {
    return `${t_('Image not saved')} · ${t_(FAILURE_TEXT[evidence.failure] || FAILURE_TEXT.SAVE_ERROR)}`
  }
  return ''
}

const confidenceText = (evidence, t_) => (typeof evidence.confidence === 'number'
  ? t_('Confidence {{value}} %', { value: Math.round(evidence.confidence * 100) })
  : t_('Confidence: not available'))

const facesText = (evidence, t_) => (evidence.facesBlurred === true
  ? t_('Faces blurred ({{count}})', { count: evidence.facesFound })
  : t_('Faces not blurred'))

// RN160: the box is not in the pixels; it is drawn over the image from Sun's data (relative to its width and height),
// dashed when it is approximate (RN154, Gemma4).
// C17b (Registro v4): the short confirmation of deleting, the same for one image or several: how many, that it cannot be undone
// and that the incidents stay.
export const DeleteConfirmation = ({ open, title, several = false, onCancel, onConfirm, t_ }) => {
  const titleId = useId()
  const lineId = useId()
  return (
    <Dialog open={open} onClose={onCancel} aria-labelledby={titleId} aria-describedby={lineId} PaperProps={{ sx: { borderRadius: 3, maxWidth: 420 } }}>
      <DialogTitle id={titleId} sx={{ fontSize: 18, fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography id={lineId} variant='body2' sx={{ color: '#CFCFCF' }}>
          {several ? t_("It can't be undone. The incidents stay.") : t_("It can't be undone. The incident stays.")}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant='outlined' onClick={onCancel} sx={{ borderRadius: 999 }}>{t_('Cancel')}</Button>
        <Button variant='contained' onClick={onConfirm} sx={{ borderRadius: 999, bgcolor: DELETE_COLOUR, color: '#1F1F1F', '&:hover': { bgcolor: DELETE_COLOUR } }}>{t_('Delete')}</Button>
      </DialogActions>
    </Dialog>
  )
}

const ImageWithBox = ({ url, evidence, t_ }) => (
  <Box role='img' aria-label={t_('Phone image')} sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', bgcolor: '#1E1E1E' }}>
    <img src={url} alt='' style={{ display: 'block', width: '100%' }} />
    {evidence.box && (
      <svg viewBox='0 0 1 1' preserveAspectRatio='none' aria-hidden='true' style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <rect
          data-testid='phone-evidence-box'
          x={evidence.box.x}
          y={evidence.box.y}
          width={evidence.box.w}
          height={evidence.box.h}
          fill='none'
          stroke={BOX_COLOUR}
          strokeWidth={3}
          vectorEffect='non-scaling-stroke'
          {...(evidence.boxApproximate === true ? { strokeDasharray: '8 6' } : {})}
        />
      </svg>
    )}
  </Box>
)

/**
 * Story 213, REAPERTURA 5 (C17.7, RN156-RN161; design Registro v3) — the "Phone image" block of the detail of a phone
 * incident, painted only when the incident brings the `evidence` key (ADMIN and SUPERADMIN, RN158). The image is asked for
 * only when "Show image" is pressed, as a blob shown through an object URL that is revoked when the block goes away; nothing
 * of it is kept in the browser (R-64). "Viewed by" is asked for on opening the block and again once the image is shown, so
 * the view just made appears (H-C17-10).
 */
const PhoneEvidence = ({ incident, now, dateFormat, canDelete = false, onDeleted = () => {}, onGone = () => {}, t_ }) => {
  const evidence = incident.evidence
  const headingId = useId()
  const [views, setViews] = useState(NO_VIEWS)
  const [image, setImage] = useState({ url: '', refusal: 0 })
  const [enlarged, setEnlarged] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleteNotice, setDeleteNotice] = useState('')
  const mountedRef = useRef(true)

  const askViews = useCallback(() => {
    getEvidenceViews(incident.id)
      .then((answer) => {
        if (mountedRef.current) {
          setViews(answer && Array.isArray(answer.items) ? answer : NO_VIEWS)
        }
      })
      .catch(() => {
        if (mountedRef.current) {
          setViews(NO_VIEWS)
        }
      })
  }, [incident.id])

  useEffect(() => {
    mountedRef.current = true
    if (evidence) {
      askViews()
    }
    return () => { mountedRef.current = false }
  }, [evidence, askViews])

  // Let the image go when the block goes away (closing the panel or opening another incident).
  useEffect(() => () => {
    if (image.url) {
      window.URL.revokeObjectURL(image.url)
    }
  }, [image.url])

  // C17b (S410): once the image is no longer available (deleted here), the one shown is let go.
  const stillAvailable = Boolean(evidence) && evidence.status === 'AVAILABLE'
  useEffect(() => {
    if (!stillAvailable) {
      setImage((previous) => (previous.url ? { url: '', refusal: 0 } : previous))
    }
  }, [stillAvailable])

  // C17b (S410, RN166): delete the image of this incident, after confirming; the block then says who deleted it and when.
  const deleteImage = () => {
    setConfirmingDelete(false)
    deleteEvidenceImage(incident.id)
      .then((updated) => {
        if (mountedRef.current) {
          setDeleteNotice('')
        }
        onDeleted(updated)
      })
      .catch((error) => {
        const status = (error && error.status) || 500
        if (mountedRef.current) {
          setDeleteNotice(refusalText(status, t_))
        }
        // S414: the image was no longer there (deleted by someone else or expired): the page shows its real state.
        if (status === 404) {
          onGone()
        }
      })
  }

  const showImage = () => {
    getEvidenceImage(incident.id)
      .then((blob) => {
        if (!mountedRef.current) {
          return
        }
        setImage({ url: window.URL.createObjectURL(blob), refusal: 0 })
        askViews()
      })
      .catch((error) => {
        if (mountedRef.current) {
          setImage({ url: '', refusal: (error && error.status) || 500 })
        }
      })
  }

  const available = Boolean(evidence) && evidence.status === 'AVAILABLE'
  const unavailable = image.refusal === 404
  const refusal = image.refusal ? refusalText(image.refusal, t_) : ''
  const canShow = available && !image.url && !unavailable && image.refusal !== 403
  const more = views.total - views.items.length

  const data = available && (
    <Box component='dl' sx={{ m: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
      <Box>
        <Typography component='dt' variant='caption' sx={{ color: '#A8A8A8' }}>{t_('Confidence')}</Typography>
        <Typography component='dd' variant='body2' sx={{ m: 0, mt: 0.25 }}>{confidenceText(evidence, t_)}</Typography>
      </Box>
      <Box>
        <Typography component='dt' variant='caption' sx={{ color: '#A8A8A8' }}>{t_('Faces')}</Typography>
        <Typography component='dd' variant='body2' sx={{ m: 0, mt: 0.25 }}>{facesText(evidence, t_)}</Typography>
      </Box>
      {evidence.boxApproximate === true && (
        <Typography component='dd' variant='body2' sx={{ m: 0, gridColumn: '1 / -1', color: '#FFCC80' }}>{t_('Approximate box')}</Typography>
      )}
      <Typography component='dd' sx={{ m: 0, gridColumn: '1 / -1', color: '#A8A8A8', fontSize: 13 }}>
        {t_('Deleted on {{date}}', { date: formatDate(new Date(evidence.expiresAt), dateFormat) })}
      </Typography>
    </Box>
  )

  return (
    <Stack component='section' aria-labelledby={headingId} spacing={1.25} sx={{ borderTop: '1px solid #3A3A3A', pt: 2 }}>
      <Typography id={headingId} component='h3' sx={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF' }}>{t_('Phone image')}</Typography>
      {canShow && (
        <Button variant='outlined' onClick={showImage} sx={{ alignSelf: 'flex-start', borderRadius: 999, minHeight: 40, px: 2.25 }}>
          {t_('Show image')}
        </Button>
      )}
      {image.url && (
        <Box sx={{ position: 'relative' }}>
          <ImageWithBox url={image.url} evidence={evidence} t_={t_} />
          <IconButton
            aria-label={t_('Enlarge')}
            onClick={() => setEnlarged(true)}
            sx={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, bgcolor: 'rgba(20, 20, 20, 0.6)', color: '#FFFFFF' }}
          >
            <ZoomInRounded fontSize='small' />
          </IconButton>
        </Box>
      )}
      {refusal && <Typography variant='body2' role='status' sx={{ color: '#CFCFCF' }}>{refusal}</Typography>}
      {data}
      {available && canDelete && (
        <Button
          variant='text'
          onClick={() => setConfirmingDelete(true)}
          sx={{ alignSelf: 'flex-start', minHeight: 36, px: 0.5, color: DELETE_COLOUR, fontWeight: 600, textTransform: 'none' }}
        >
          {t_('Delete image')}
        </Button>
      )}
      {deleteNotice && <Typography variant='body2' role='status' sx={{ color: '#CFCFCF' }}>{deleteNotice}</Typography>}
      {!available && (
        <Typography data-testid='phone-evidence-line' variant='body2' sx={{ color: '#CFCFCF' }}>{lineWithoutImage(evidence, dateFormat, t_)}</Typography>
      )}
      {views.items.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant='caption' sx={{ color: '#A8A8A8' }}>{t_('Viewed by')}</Typography>
          <Box component='ul' aria-label={t_('Viewed by')} sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {views.items.map((view, index) => (
              <Typography component='li' key={index} variant='body2' sx={{ fontSize: 13 }}>
                {`${view.username} · ${whenText(view.viewedAt, { now, dateFormat, t_ })} · ${view.clientIp} · ${view.browser || t_('Unknown')}`}
              </Typography>
            ))}
          </Box>
          {more > 0 && <Typography variant='caption' sx={{ color: '#A8A8A8' }}>{t_('and {{count}} more', { count: more })}</Typography>}
        </Stack>
      )}
      <DeleteConfirmation open={confirmingDelete} title={t_('Delete this image?')} onCancel={() => setConfirmingDelete(false)} onConfirm={deleteImage} t_={t_} />
      <Dialog
        open={enlarged && Boolean(image.url)}
        onClose={() => setEnlarged(false)}
        fullScreen
        PaperProps={{ 'aria-label': t_('Phone image'), sx: { bgcolor: 'rgba(0, 0, 0, 0.85)', alignItems: 'center', justifyContent: 'center', gap: 1.75, p: 3 } }}
      >
        <IconButton aria-label={t_('Close')} onClick={() => setEnlarged(false)} sx={{ position: 'absolute', top: 16, right: 16, color: '#FFFFFF' }}>
          <CloseRounded />
        </IconButton>
        {image.url && (
          <Box sx={{ width: 'min(960px, 100%)' }}>
            <ImageWithBox url={image.url} evidence={evidence} t_={t_} />
          </Box>
        )}
        {available && (
          <Stack direction='row' spacing={2} sx={{ color: '#E6E6E6' }}>
            <span>{confidenceText(evidence, t_)}</span>
            <span>{facesText(evidence, t_)}</span>
            {evidence.boxApproximate === true && <span style={{ color: '#FFCC80' }}>{t_('Approximate box')}</span>}
          </Stack>
        )}
      </Dialog>
    </Stack>
  )
}

export default PhoneEvidence
